import { Section, SectionHeading, FadeIn } from "@/components/Section";
import { ShieldCheck, BadgePercent, Handshake, Sparkles, AlertCircle } from "lucide-react";

const STEPS = [
  {
    n: "01",
    title: "Stringent vetting & quality criteria",
    desc: "Every vendor is screened against licensure, compliance, fulfillment SLAs, and clinical-grade quality standards before they ever appear in the network.",
    icon: ShieldCheck,
  },
  {
    n: "02",
    title: "Nodera-negotiated contracted rates",
    desc: "We aggregate clinic demand across the network and negotiate wholesale rates no single clinic could secure alone — locked in by contract.",
    icon: BadgePercent,
  },
  {
    n: "03",
    title: "Direct vendor–clinic relationships",
    desc: "Clinics work directly with their vendors and keep the patient relationship. Nodera doesn't sit between you and care delivery — we sit underneath it.",
    icon: Handshake,
  },
  {
    n: "04",
    title: "Predictable program-level economics",
    desc: "Contracted rates make program margins predictable from day one — so clinics can scale recurring revenue without guesswork on cost-of-goods.",
    icon: Sparkles,
  },
];

export default function VendorNetworkAdvantage() {
  return (
    <Section testId="vendor-network-advantage-section" className="bg-white">
      <SectionHeading
        eyebrow="The Nodera Vendor Network"
        title="A curated vendor network — at rates no single clinic could negotiate alone."
        intro="We've built the network the way clinics wish they could: a stringent vetting process for every partner, and aggregated buying power that produces highly competitive contracted rates passed straight through to Nodera customers."
      />

      <div className="mt-14 grid lg:grid-cols-4 gap-5 relative">
        {/* Connecting line */}
        <div className="hidden lg:block absolute top-[38px] left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

        {STEPS.map((s, i) => (
          <FadeIn key={s.n} delay={i * 0.08}>
            <div
              data-testid={`vendor-network-step-${i + 1}`}
              className="relative rounded-2xl border border-slate-200 bg-white p-6 h-full hover:border-blue-200 transition-colors"
            >
              <div className="relative z-10 w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center font-display font-bold">
                {s.n}
              </div>
              <div className="mt-5 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600">
                <s.icon className="w-3.5 h-3.5" /> Step {i + 1}
              </div>
              <h3 className="mt-2 font-display text-lg font-semibold text-slate-900 leading-snug">
                {s.title}
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </div>
          </FadeIn>
        ))}
      </div>

      <FadeIn delay={0.3}>
        <div className="mt-14 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 rounded-2xl border border-slate-200 bg-slate-50 p-8">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600">Our role</div>
            <h3 className="mt-2 font-display text-2xl font-semibold text-slate-900">
              We curate the network. You keep the relationship.
            </h3>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm text-slate-700">
              {[
                "Vet vendors against quality + compliance criteria",
                "Negotiate competitive contracted rates",
                "Pass wholesale economics through to clinics",
                "Coordinate vendor fulfillment + SLAs",
                "Embed repeatable program workflows",
                "Support recurring revenue structures",
              ].map((t, i) => (
                <li key={t} className="flex items-start gap-2">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-slate-900" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5 rounded-2xl border border-amber-200 bg-amber-50/50 p-8">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-amber-700">
              <AlertCircle className="w-3.5 h-3.5" /> Important
            </div>
            <h3 className="mt-2 font-display text-lg font-semibold text-slate-900">
              What Nodera does not do.
            </h3>
            <p className="mt-2 text-sm text-slate-700 leading-relaxed">
              The platform does not set patient pricing, provide medical services, or participate in clinical decision-making. Clinics retain full control of clinical and commercial decisions — Nodera's job is to make the supply side faster, cheaper, and easier to operate.
            </p>
          </div>
        </div>
      </FadeIn>
    </Section>
  );
}
